import { ATTRIBUTION_URL_PARAMS } from '../attribution';

/**
 * Google Analytics 4. The app has its own web data stream ("K3X App") next to the marketing
 * site's: Enhanced Measurement's history page views stay on for `k3x.ai` (Framer) and are off
 * here, where `trackGoogleAnalyticsPageView` sends id-free page views instead. Both sites share
 * the `_ga` cookie on `.k3x.ai`, so a visitor keeps one client id across them without a linker.
 * Loaded only in production builds, like Crazy Egg and PixelFlow, so localhost, Playwright and
 * staging never show up as users or conversions.
 */
const GA_MEASUREMENT_ID = 'G-B3V4KXS8GH';
const GA_SCRIPT_URL = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;

/** Google Ads click ids GA4 reads for attribution, on top of the first-touch set. */
const GA_EXTRA_CAMPAIGN_PARAMS = ['utm_id', 'gbraid', 'wbraid'];

const isGoogleAnalyticsEnabled = () => import.meta.env.VITE_ENV === 'production';

const gtagIfLoaded = (): ((...args: unknown[]) => void) | undefined =>
  isGoogleAnalyticsEnabled() && typeof window.gtag === 'function' ? window.gtag : undefined;

/**
 * Campaign parameters from the landing URL, kept for the first page view only. Page views
 * otherwise report the route template with no query string, but GA4 attributes the session from
 * the first hit's `page_location`, so dropping `utm_*` / `gclid` there would lose the campaign.
 */
let landingCampaignQuery = '';
let lastPageLocation: string | undefined;
let lastPathname: string | undefined;
let identifiedUserId: string | null = null;
let pendingSignUp: SignUpMethod | undefined;

const pickCampaignQuery = (search: string): string => {
  const source = new URLSearchParams(search);
  const kept = new URLSearchParams();

  for (const key of [...ATTRIBUTION_URL_PARAMS, ...GA_EXTRA_CAMPAIGN_PARAMS]) {
    const value = source.get(key);
    if (value) {
      kept.set(key, value);
    }
  }

  const query = kept.toString();
  return query ? `?${query}` : '';
};

/**
 * The stock gtag.js snippet. Must run before the router mounts, while the landing URL still
 * carries its campaign parameters. The automatic page view is off — `trackGoogleAnalyticsPageView`
 * sends one per route instead, with the route's title and an id-free location.
 */
export const initGoogleAnalytics = () => {
  if (!isGoogleAnalyticsEnabled()) {
    return;
  }

  if (document.querySelector(`script[src="${GA_SCRIPT_URL}"]`)) {
    return;
  }

  landingCampaignQuery = pickCampaignQuery(window.location.search);

  const script = document.createElement('script');
  script.async = true;
  script.src = GA_SCRIPT_URL;
  document.head.appendChild(script);

  const dataLayer = (window.dataLayer = window.dataLayer || []);
  // gtag.js reads the `arguments` object off the queue, not an array, so this stays a plain
  // function that pushes `arguments`; the rest parameter only types the call sites.
  const gtag = function gtag(..._args: unknown[]) {
    dataLayer.push(arguments);
  };
  window.gtag = gtag;

  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
};

/**
 * GA4 `user_id` = the K3X user id, the same id `identifyPayticsUser` hands the Paytics GA4 sync
 * for Stripe's events, so both land on one user. `null` clears it on sign-out.
 * `set`, not a second `config`: the stream's Google tag has *Ignore duplicate instances of on-page
 * configuration* on, so gtag drops every `config` after the first one and the id never went out.
 * Runs on every authenticated navigation, so it is set only when the user changes.
 */
export const identifyGoogleAnalyticsUser = (userId: string | null) => {
  const gtag = gtagIfLoaded();
  if (!gtag || userId === identifiedUserId) {
    return;
  }

  identifiedUserId = userId;
  gtag('set', { user_id: userId });
};

/**
 * One `page_view` per pathname change. `path` is the route template (`/leads/$leadId`), so
 * every lead, agent, search and import reports as one page; search-param-only changes (drawers,
 * filters) do not count as page views. `set` makes the same id-free location and title apply to
 * every later event on the page, including the custom events below.
 */
export const trackGoogleAnalyticsPageView = (page: { title: string; path: string }) => {
  const gtag = gtagIfLoaded();
  if (!gtag || window.location.pathname === lastPathname) {
    return;
  }

  lastPathname = window.location.pathname;

  const pageLocation = `${window.location.origin}${page.path}${landingCampaignQuery}`;
  landingCampaignQuery = '';

  const pageReferrer = lastPageLocation;
  lastPageLocation = pageLocation;

  gtag('set', { page_location: pageLocation, page_title: page.title });
  gtag('event', 'page_view', {
    page_location: pageLocation,
    page_title: page.title,
    ...(pageReferrer ? { page_referrer: pageReferrer } : {}),
  });

  if (pendingSignUp) {
    gtag('event', 'sign_up', { method: pendingSignUp });
    pendingSignUp = undefined;
  }
};

export type SignUpMethod = 'email' | 'google' | 'microsoft';

/**
 * GA4's recommended `sign_up`, sent from the browser so it carries the session's traffic source.
 * An email link opened in a new tab signs up before the router's first page view; GA4 would start
 * that session from `sign_up`, whose location no longer has the campaign, and call it direct — so
 * until the first page view it waits and goes out right after it.
 */
export const trackSignUp = (method: SignUpMethod) => {
  const gtag = gtagIfLoaded();
  if (!gtag) {
    return;
  }

  if (!lastPageLocation) {
    pendingSignUp = method;
    return;
  }

  gtag('event', 'sign_up', { method });
};

/**
 * Product actions (the names `trackAction` sends to Sentry) that are also GA4 events. Driven by
 * mutation `meta.track` in the query client and by `CallProvider`, so a form never calls GA.
 */
const GA_EVENTS_BY_ACTION: Record<string, string> = {
  'email.sent': 'email_sent',
  'lead.created': 'lead_created',
  'call.started': 'call_started',
};

export const trackGoogleAnalyticsAction = (action: string) => {
  const event = GA_EVENTS_BY_ACTION[action];
  const gtag = gtagIfLoaded();
  if (!event || !gtag) {
    return;
  }

  gtag('event', event);
};

export type CommandUsed = {
  /**
   * `free_text` for anything typed, the suggestion category (`communicate`, `enrich`, ...) for a
   * suggestion chip, or a fixed name for a one-click shortcut (`import_fix`, `agent_builder`).
   * Never the prompt itself: it is user content and would leak PII into GA.
   */
  command: string;
  source: 'typed' | 'suggestion' | 'shortcut';
  thread: 'new' | 'existing';
};

/** A message sent to the Command Center (AI assistant). */
export const trackCommandUsed = ({ command, source, thread }: CommandUsed) => {
  gtagIfLoaded()?.('event', 'command_used', { command, source, thread });
};
